import { LoadingOutlined, ReloadOutlined, PlusCircleOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import * as QueryString from 'query-string';
import { useLocation } from 'react-router';
import { Button, Card, Col, Divider, message, Modal, Row } from 'antd';
import {
    useCreateIngestionExecutionRequestMutation,
    useCreateIngestionSourceMutation,
    useDeleteIngestionSourceMutation,
    useListIngestionSourcesQuery,
    useUpdateIngestionSourceMutation,
} from '../../../graphql/ingestion.generated';
import { Message } from '../../shared/Message';
import { IngestionSourceBuilderModal } from './builder/IngestionSourceBuilderModal';
import { addToListIngestionSourcesCache, CLI_EXECUTOR_ID, removeFromListIngestionSourcesCache } from './utils';
import { DEFAULT_EXECUTOR_ID, SourceBuilderState, StringMapEntryInput } from './builder/types';
import { IngestionSource, UpdateIngestionSourceInput } from '../../../types.generated';
import { SearchBar } from '../../search/SearchBar';
import { useEntityRegistry } from '../../useEntityRegistry';
import { ExecutionDetailsModal } from './executions/ExecutionRequestDetailsModal';
import RecipeViewerModal from './RecipeViewerModal';
import IngestionSourceTable from './IngestionSourceTable';
import { scrollToTop } from '../../shared/searchUtils';
import useRefreshIngestionData from './executions/useRefreshIngestionData';
import { isExecutionRequestActive } from './executions/IngestionSourceExecutionList';
import analytics, { EventType } from '../../analytics';
import { usePolling } from './hooks/usePolling';
import { useTranslation } from '../../../i18n/useTranslation';
import { PaginationBar } from '../../entity/shared/tabs/Glossary/PaginationBar';
import { ComponentSpinner } from '../../shared/ComponentSpinner';

const PLACEHOLDER_URN = 'placeholder-urn';

export enum IngestionSourceType {
    ALL,
    UI,
    CLI,
}

export function shouldIncludeSource(source: any, sourceFilter: IngestionSourceType) {
    if (sourceFilter === IngestionSourceType.CLI) {
        return source.config.executorId === CLI_EXECUTOR_ID;
    }
    if (sourceFilter === IngestionSourceType.UI) {
        return source.config.executorId !== CLI_EXECUTOR_ID;
    }
    return true;
}

const DEFAULT_PAGE_SIZE = 10;

const removeExecutionsFromIngestionSource = (source) => {
    if (source) {
        return {
            name: source.name,
            type: source.type,
            recipeVersion: source.recipeVersion ?? 'v1',
            applianceId: source.applianceId,
            displayName: source.displayName,
            schedule: source.schedule,
            config: source.config,
        };
    }
    return undefined;
};

const POLLING_INTERVAL = 5000; // 5 seconds
const IDLE_POLLING_INTERVAL = 30000; // 30 seconds when no active tasks

const SCHEDULED = 'SCHEDULED';
const RUNNING = 'RUNNING';

export const IngestionSourceList = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const entityRegistry = useEntityRegistry();
    const params = QueryString.parse(location.search, { arrayFormat: 'comma' });
    const paramsQuery = (params?.query as string) || undefined;

    const [query, setQuery] = useState<undefined | string>(undefined);
    const [isRefreshed, setIsRefreshed] = useState(false);

    useEffect(() => setQuery(paramsQuery), [paramsQuery]);

    const [page, setPage] = useState(1);

    const pageSize = DEFAULT_PAGE_SIZE;
    const start = (page - 1) * pageSize;

    const [isBuildingSource, setIsBuildingSource] = useState<boolean>(false);
    const [isViewingRecipe, setIsViewingRecipe] = useState<boolean>(false);
    const [focusSourceUrn, setFocusSourceUrn] = useState<undefined | string>(undefined);
    const [focusExecutionUrn, setFocusExecutionUrn] = useState<undefined | string>(undefined);
    const [lastRefresh, setLastRefresh] = useState(0);
    // Set of removed urns used to account for eventual consistency
    const [removedUrns, setRemovedUrns] = useState<string[]>([]);
    // const [sourceFilter, setSourceFilter] = useState(IngestionSourceType.ALL);

    // Ingestion Source Queries
    const { loading, error, data, client, refetch } = useListIngestionSourcesQuery({
        variables: {
            input: {
                start,
                count: pageSize,
                query,
            },
        },
        fetchPolicy: 'cache-first',
    });
    const [createIngestionSource] = useCreateIngestionSourceMutation();
    const [updateIngestionSource] = useUpdateIngestionSourceMutation();

    // Execution Request queries
    const [createExecutionRequestMutation] = useCreateIngestionExecutionRequestMutation();
    const [removeIngestionSourceMutation] = useDeleteIngestionSourceMutation();

    const totalSources = data?.listIngestionSources?.total || 0;
    const sources = data?.listIngestionSources?.ingestionSources || [];
    const filteredSources = sources.filter(
        (source) => !removedUrns.includes(source.urn) && shouldIncludeSource(source, IngestionSourceType.ALL),
    ) as IngestionSource[];
    const focusSource =
        (focusSourceUrn && filteredSources.find((source) => source.urn === focusSourceUrn)) || undefined;

    const onRefresh = useCallback(() => {
        setIsRefreshed(true);

        refetch()?.finally(() => {
            setIsRefreshed(false);
        });
        // Used to force a re-render of the child execution request list.
        setLastRefresh(new Date().getTime());
    }, [refetch]);

    const hasActiveOrScheduledSources = useCallback(() => {
        return filteredSources.some(
            (source) =>
                [SCHEDULED, RUNNING].includes(source.currentStatus || '') ||
                source.executions?.executionRequests.some((request) => isExecutionRequestActive(request)),
        );
    }, [filteredSources]);

    // Use the custom polling hook
    const { startPolling } = usePolling({
        onPoll: () => {
            refetch();
        },
        shouldPollCondition: hasActiveOrScheduledSources,
        activeInterval: POLLING_INTERVAL,
        idleInterval: IDLE_POLLING_INTERVAL,
        data,
        stopDelay: 1000,
    });

    function hasActiveExecution() {
        return !!filteredSources.find((source) =>
            source.executions?.executionRequests.find((request) => isExecutionRequestActive(request)),
        );
    }
    useRefreshIngestionData(onRefresh, hasActiveExecution);

    const executeIngestionSource = (urn: string) => {
        createExecutionRequestMutation({
            variables: {
                input: {
                    ingestionSourceUrn: urn,
                },
            },
        })
            .then(() => {
                analytics.event({
                    type: EventType.ExecuteIngestionSourceEvent,
                });
                message.success({
                    content: t('Successfully submitted ingestion execution request!'),
                    duration: 3,
                });
                startPolling(); // Start polling when execution starts
                setTimeout(() => onRefresh(), 3000);
            })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: `Failed to submit ingestion execution request!: \n ${e.message || ''}`,
                    duration: 3,
                });
            });
    };

    const onCreateOrUpdateIngestionSourceSuccess = () => {
        setTimeout(() => refetch(), 2000);
        setIsBuildingSource(false);
        setFocusSourceUrn(undefined);
    };

    const formatExtraArgs = (extraArgs): StringMapEntryInput[] => {
        if (extraArgs === null || extraArgs === undefined) return [];
        return extraArgs.map((entry) => ({ key: entry.key, value: entry.value }));
    };

    const createOrUpdateIngestionSource = (
        input: UpdateIngestionSourceInput,
        resetState: () => void,
        shouldRun?: boolean,
    ) => {
        if (focusSourceUrn) {
            // Update:
            updateIngestionSource({ variables: { urn: focusSourceUrn as string, input } })
                .then(() => {
                    analytics.event({
                        type: EventType.UpdateIngestionSourceEvent,
                        sourceType: input.type,
                        interval: input.schedule?.interval,
                    });
                    message.success({
                        content: t('Successfully updated ingestion source!'),
                        duration: 3,
                    });
                    onCreateOrUpdateIngestionSourceSuccess();
                    resetState();
                    if (shouldRun) executeIngestionSource(focusSourceUrn);
                })
                .catch((e) => {
                    message.destroy();
                    message.error({
                        content: `Failed to update ingestion source!: \n ${e.message || ''}`,
                        duration: 3,
                    });
                });
        } else {
            // Create
            createIngestionSource({ variables: { input } })
                .then((result) => {
                    message.loading({ content: 'Loading...', duration: 2 });
                    const newSource = {
                        urn: result?.data?.createIngestionSource || PLACEHOLDER_URN,
                        name: input.displayName,
                        type: input.type,
                        config: null,
                        schedule: {
                            interval: input.schedule?.interval || null,
                            timezone: input.schedule?.timezone || null,
                        },
                        platform: null,
                        executions: null,
                    };
                    addToListIngestionSourcesCache(client, newSource, pageSize, query);
                    setTimeout(() => {
                        refetch();
                        analytics.event({
                            type: EventType.CreateIngestionSourceEvent,
                            sourceType: input.type,
                            interval: input.schedule?.interval,
                        });
                        message.success({
                            content: t('Successfully created ingestion source!'),
                            duration: 3,
                        });
                        if (shouldRun && result.data?.createIngestionSource) {
                            executeIngestionSource(result.data.createIngestionSource);
                        }
                    }, 2000);
                    setIsBuildingSource(false);
                    setFocusSourceUrn(undefined);
                    resetState();
                })
                .catch((e) => {
                    message.destroy();
                    message.error({
                        content: `Failed to create ingestion source!: \n ${e.message || ''}`,
                        duration: 3,
                    });
                });
        }
    };

    const onChangePage = (newPage: number) => {
        scrollToTop();
        setPage(newPage);
    };

    const deleteIngestionSource = (urn: string) => {
        removeFromListIngestionSourcesCache(client, urn, page, pageSize, query);
        removeIngestionSourceMutation({
            variables: { urn },
        })
            .then(() => {
                analytics.event({
                    type: EventType.DeleteIngestionSourceEvent,
                });
                message.success({ content: t('Removed ingestion source.'), duration: 2 });
                const newRemovedUrns = [...removedUrns, urn];
                setRemovedUrns(newRemovedUrns);
                setTimeout(() => {
                    refetch?.();
                }, 3000);
            })
            .catch((e: unknown) => {
                message.destroy();
                if (e instanceof Error) {
                    message.error({
                        content: t('Failed to remove ingestion source: \n {{message}}', { message: e.message || '' }),
                        duration: 3,
                    });
                }
            });
    };

    const onSubmit = (recipeBuilderState: SourceBuilderState, resetState: () => void, shouldRun?: boolean) => {
        createOrUpdateIngestionSource(
            {
                type: recipeBuilderState.type as string,
                name: (recipeBuilderState.name as string) || '',
                displayName: recipeBuilderState.displayName as string,
                applianceId: recipeBuilderState.applianceId as string,
                recipeVersion: recipeBuilderState.recipeVersion as string,
                config: {
                    recipe: recipeBuilderState.config?.recipe as string,
                    version:
                        (recipeBuilderState.config?.version?.length &&
                            (recipeBuilderState.config?.version as string)) ||
                        undefined,
                    executorId:
                        (recipeBuilderState.config?.executorId?.length &&
                            (recipeBuilderState.config?.executorId as string)) ||
                        DEFAULT_EXECUTOR_ID,
                    debugMode: recipeBuilderState.config?.debugMode || false,
                    extraArgs: formatExtraArgs(recipeBuilderState.config?.extraArgs || []),
                },
                schedule: recipeBuilderState.schedule && {
                    interval: recipeBuilderState.schedule?.interval as string,
                    timezone: recipeBuilderState.schedule?.timezone as string,
                },
            },
            resetState,
            shouldRun,
        );
    };

    const onEdit = (urn: string) => {
        setIsBuildingSource(true);
        setFocusSourceUrn(urn);
    };

    const onView = (urn: string) => {
        setIsViewingRecipe(true);
        setFocusSourceUrn(urn);
    };

    const onExecute = (urn: string) => {
        Modal.confirm({
            title: t('Confirm Source Execution'),
            content: t("Click 'Execute' to run this ingestion source."),
            onOk() {
                startPolling(); // Start polling before execution
                executeIngestionSource(urn);
            },
            onCancel() {},
            okText: t('Execute'),
            maskClosable: true,
            closable: true,
            centered: true,
        });
    };

    const onDelete = (urn: string) => {
        Modal.confirm({
            title: t('Confirm Ingestion Source Removal'),
            content: t(
                'Are you sure you want to remove this ingestion source? Removing will terminate any scheduled ingestion runs.',
            ),
            onOk() {
                deleteIngestionSource(urn);
            },
            onCancel() {},
            okText: t('Yes'),
            okType: 'danger',
            maskClosable: true,
            centered: true,
        });
    };

    const onCancel = (resetState?: () => void) => {
        setIsBuildingSource(false);
        setIsViewingRecipe(false);
        setFocusSourceUrn(undefined);
        resetState?.();
    };

    return (
        <>
            {error && <Message type="error" content={error.message} />}
            <Card className="f-rounded-corner-1 pa-0 overflow-hidden">
                <Card className="f-rounded-corner-0 px-3 py-2">
                    <Row className="f-gap-3" align="middle">
                        <Col flex="auto">
                            <SearchBar
                                suggestions={[]}
                                inputStyle={{
                                    height: 32,
                                    fontSize: 14,
                                }}
                                hideRecommendations
                                onSearch={() => null}
                                initialQuery={query || ''}
                                entityRegistry={entityRegistry}
                                placeholderText={t('Search sources')}
                                onQueryChange={(q) => {
                                    setQuery(q);
                                    setPage(1);
                                }}
                            />
                        </Col>
                        <Col flex="none">
                            <Button
                                type="text"
                                className="f-btn-flat"
                                aria-label="Create new source"
                                // id={INGESTION_CREATE_SOURCE_ID}
                                onClick={() => setIsBuildingSource(true)}
                                data-testid="create-ingestion-source-button"
                            >
                                <PlusCircleOutlined aria-hidden="true" />
                                <span>{t('Create New Source')}</span>
                            </Button>
                        </Col>
                        <Col flex="none">
                            <Button
                                type="text"
                                onClick={onRefresh}
                                aria-label="Refresh"
                                className="f-btn-flat"
                                disabled={isRefreshed}
                            >
                                {isRefreshed ? (
                                    <LoadingOutlined aria-hidden="true" />
                                ) : (
                                    <ReloadOutlined aria-hidden="true" />
                                )}
                                <span>{t('Refresh')}</span>
                            </Button>
                        </Col>
                    </Row>
                </Card>
                <Divider className="ma-0" />
                <div className="f-info-cards">
                    <div>
                        <div className="f-ic-title">{t('Sources')}</div>
                        <div className="f-ic-content">
                            <div className="f-ic-value">{totalSources}</div>
                        </div>
                    </div>
                </div>
                {!data && loading && <ComponentSpinner />}
                <Divider className="ma-0" />
                {/* TODO: Commenting this for now */}
                {/* <StyledSelect
                        value={sourceFilter}
                        onChange={(selection) => setSourceFilter(selection as IngestionSourceType)}
                    >
                        <Select.Option value={IngestionSourceType.ALL}>All</Select.Option>
                        <Select.Option value={IngestionSourceType.UI}>UI</Select.Option>
                        <Select.Option value={IngestionSourceType.CLI}>CLI</Select.Option>
                    </StyledSelect> */}
                <IngestionSourceTable
                    lastRefresh={lastRefresh}
                    sources={filteredSources || []}
                    setFocusExecutionUrn={setFocusExecutionUrn}
                    onExecute={onExecute}
                    onEdit={onEdit}
                    onView={onView}
                    onDelete={onDelete}
                    onRefresh={onRefresh}
                />
                <Divider className="ma-0" />
                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    loading={loading}
                    pageSizeOptions={[]}
                    totalCount={totalSources}
                    onChangePage={onChangePage}
                    onShowSizeChange={() => {}}
                />
            </Card>
            <IngestionSourceBuilderModal
                initialState={removeExecutionsFromIngestionSource(focusSource)}
                visible={isBuildingSource}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
            {isViewingRecipe && <RecipeViewerModal recipe={focusSource?.config.recipe} onCancel={onCancel} />}
            {focusExecutionUrn && (
                <ExecutionDetailsModal
                    urn={focusExecutionUrn}
                    visible
                    onClose={() => setFocusExecutionUrn(undefined)}
                />
            )}
        </>
    );
};
